"use client";

import {
  CSSProperties,
  Fragment,
  JSX,
  type MouseEvent as ReactMouseEvent,
  useId,
} from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { donutSlicePath, labelArcPath } from "@/lib/svg-geometry";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import type { ModuleCategory, ModuleEcosystemMapData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { AnimatedTabPanel } from "@/components/motion/animated-tab-panel";
import { importsTokenScope } from "./imports-token-scope";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";

const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };

type SegmentStatus = "active" | "inactive";

interface EcosystemSegment {
  id: string;
  label: string;
  status?: SegmentStatus;
  categoryId?: string;
}

interface EcosystemRing {
  label: string;
  segments: EcosystemSegment[];
}

interface EcosystemData {
  core: { lines: string[] };
  foundation: EcosystemRing;
  products: EcosystemRing;
  marketplace: { label: string };
  categories?: ModuleCategory[];
}

type EcosystemCategory = {
  id: string;
  label: string;
  shortLabel: string;
  color: string;
  infoColor: string;
  outline: boolean;
};

interface HoveredSegment {
  id: string;
  ring: string;
  label: string;
  categoryId: string | undefined;
}

function defaultEcosystemCategories(
  activeText: string,
  inactiveText: string,
  activeShort: string,
  availableShort: string,
): EcosystemCategory[] {
  return [
    {
      id: "active",
      label: activeText,
      shortLabel: activeShort,
      color: "var(--status-2)",
      infoColor: "var(--status-2)",
      outline: false,
    },
    {
      id: "inactive",
      label: inactiveText,
      shortLabel: availableShort,
      color: "var(--status-neutral)",
      infoColor: "var(--subtle)",
      outline: true,
    },
  ];
}

function toEcosystemCategories(categories: ModuleCategory[]): EcosystemCategory[] {
  return categories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    shortLabel: cat.label,
    color: cat.color,
    infoColor: cat.color,
    outline: false,
  }));
}

function RingSegment({
  cx,
  cy,
  innerR,
  outerR,
  midR,
  startAngle,
  endAngle,
  label,
  color,
  outline,
  isDim,
  onHover,
  onLeave,
  onClick,
  fontSize = 10,
}: {
  cx: number;
  cy: number;
  innerR: number;
  outerR: number;
  midR: number;
  startAngle: number;
  endAngle: number;
  label: string;
  color: string;
  outline: boolean;
  isDim: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick?: (event: ReactMouseEvent<SVGGElement>) => void;
  fontSize?: number;
}) {
  const uid = useId();
  const pathId = `${uid}-label`;
  const hoverGesture = useHoverGesture();

  const path = donutSlicePath(cx, cy, outerR, innerR, startAngle, endAngle);
  const lPath = labelArcPath(cx, cy, midR, startAngle, endAngle);
  const arcLength = ((endAngle - startAngle) * Math.PI * midR) / 180;
  const stripped = stripInlineMarkdown(label) ?? "";
  const estTextWidth = stripped.length * fontSize * 0.55;
  const availableWidth = Math.max(0, arcLength - 12);
  const useLengthAdjust = estTextWidth > availableWidth && availableWidth > 0;

  let fill: string;
  let fillOpacity: number;
  let stroke: string;
  let strokeDasharray: string;
  let strokeOpacity: number;
  let textColor: string;

  if (!outline) {
    fill = color; fillOpacity = 0.85;
    stroke = "none"; strokeDasharray = "0"; strokeOpacity = 1;
    textColor = "var(--bg)";
  } else {
    fill = "transparent"; fillOpacity = 1;
    stroke = color; strokeDasharray = "3 3"; strokeOpacity = 0.5;
    textColor = "var(--muted)";
  }

  return (
    <g
      onPointerEnter={(e) => {
        if (hoverGesture.enter(e)) onHover();
      }}
      onPointerLeave={(e) => {
        if (hoverGesture.leave(e)) onLeave();
      }}
      onClick={onClick}
      style={{ cursor: "pointer", transition: "opacity 0.15s" }}
      opacity={isDim ? 0.35 : 1}
    >
      <path
        d={path}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeOpacity={strokeOpacity}
        strokeWidth="1"
        strokeDasharray={strokeDasharray}
        strokeLinejoin="round"
      />
      <defs>
        <path id={pathId} d={lPath} fill="none" />
      </defs>
      <text
        fontSize={fontSize}
        fontWeight={outline ? 400 : 500}
        fill={textColor}
        style={{ pointerEvents: "none" }}
      >
        <textPath
          href={`#${pathId}`}
          startOffset="50%"
          textAnchor="middle"
          {...(useLengthAdjust
            ? { textLength: availableWidth, lengthAdjust: "spacingAndGlyphs" as const }
            : {})}
        >
          {stripped}
        </textPath>
      </text>
    </g>
  );
}

function RingCounts({
  label,
  counts,
}: {
  label: string;
  counts: Array<{ cat: EcosystemCategory; count: number }>;
}) {
  const lastIndex = counts.length - 1;
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span
        className={cn(
          "text-[var(--subtle)] min-w-[80px] leading-tight",
          MODULE_LABEL_WRAP.label,
        )}
      >
        {stripInlineMarkdown(label)}
      </span>
      {counts.map(({ cat, count }, i) =>
        i === lastIndex && counts.length > 1 ? (
          <span
            key={cat.id}
            className="text-[var(--subtle)] ml-auto"
            style={tabular}
          >
            {count} {cat.shortLabel}
          </span>
        ) : (
          <Fragment key={cat.id}>
            <span className="text-[var(--fg)] font-medium" style={tabular}>
              {count}
            </span>
            <span className="text-[var(--subtle)]">{cat.shortLabel}</span>
          </Fragment>
        ),
      )}
    </div>
  );
}

export default function ModuleEcosystemMap({
  data,
  activeText: activeTextProp,
  inactiveText: inactiveTextProp,
  activeShort: activeShortProp,
  availableShort: availableShortProp,
  grayscale,
  accent,
  className,
}: {
  data: ModuleEcosystemMapData;
  activeText?: string;
  inactiveText?: string;
  activeShort?: string;
  availableShort?: string;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const activeText = activeTextProp ?? t("active");
  const inactiveText = inactiveTextProp ?? t("not active");
  const activeShort = activeShortProp ?? t("active");
  const availableShort = availableShortProp ?? t("available");
  const eco = data as unknown as EcosystemData;

  const segSelect = useHoverSelect<HoveredSegment>({
    eq: (a, b) => a.id === b.id,
  });
  const effective = segSelect.effective;
  const uid = useId();
  const marketplacePathId = `${uid}-marketplace-label`;

  const size = 480;
  const cx = size / 2;
  const cy = size / 2;

  const rings = {
    core:        { r: 50 },
    foundation:  { innerR: 55,  outerR: 105, midR: 80  },
    products:    { innerR: 110, outerR: 170, midR: 140 },
    marketplace: { innerR: 175, outerR: 205, midR: 190 },
  };

  const foundationSegs = eco.foundation.segments;
  const productsSegs = eco.products.segments;
  const nF = foundationSegs.length;
  const nP = productsSegs.length;
  const foundationGap = 2;
  const productsGap = 1.5;

  const free = Boolean(eco.categories && eco.categories.length > 0);
  const categories = free
    ? toEcosystemCategories(eco.categories as ModuleCategory[])
    : defaultEcosystemCategories(
        activeText,
        inactiveText,
        activeShort,
        availableShort,
      );
  const segmentKey = (segment: EcosystemSegment) =>
    free ? segment.categoryId : segment.status;
  const categoryOf = (segment: EcosystemSegment) =>
    categories.find((cat) => cat.id === segmentKey(segment)) ?? null;
  const countsFor = (segments: EcosystemSegment[]) =>
    categories.map((cat) => ({
      cat,
      count: segments.filter((s) => segmentKey(s) === cat.id).length,
    }));

  const effectiveCategory = effective
    ? categories.find((cat) => cat.id === effective.categoryId) ?? null
    : null;

  const marketplacePath = labelArcPath(cx, cy, rings.marketplace.midR, -50, 50);

  return (
    <div
      className={cn("flex w-full justify-center", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex flex-col items-center gap-4 w-full max-w-[560px]">
        <div className="relative">
          <svg
            viewBox={`0 0 ${size} ${size}`}
            width={size}
            height={size}
            className="max-w-full h-auto"
            style={{ overflow: "visible" }}
          >
            <g opacity={effective ? 0.4 : 1} style={{ transition: "opacity 0.15s" }}>
              <circle
                cx={cx}
                cy={cy}
                r={rings.marketplace.outerR}
                fill="none"
                stroke="var(--status-neutral)"
                strokeWidth="1"
                strokeDasharray="3 4"
                opacity="0.5"
              />
              <circle
                cx={cx}
                cy={cy}
                r={rings.marketplace.innerR}
                fill="none"
                stroke="var(--status-neutral)"
                strokeWidth="1"
                strokeDasharray="3 4"
                opacity="0.5"
              />
              <defs>
                <path id={marketplacePathId} d={marketplacePath} fill="none" />
              </defs>
              <text fontSize="11" fill="var(--muted)" style={{ pointerEvents: "none" }}>
                <textPath
                  href={`#${marketplacePathId}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {stripInlineMarkdown(eco.marketplace.label)}
                </textPath>
              </text>
            </g>

            {productsSegs.map((segment, i) => {
              const startAngle = (360 / nP) * i + productsGap / 2;
              const endAngle = (360 / nP) * (i + 1) - productsGap / 2;
              const isH = effective?.id === segment.id;
              const isD = effective !== null && !isH;
              const seg: HoveredSegment = {
                id: segment.id,
                ring: eco.products.label,
                label: segment.label,
                categoryId: segmentKey(segment),
              };
              const cat = categoryOf(segment);
              return (
                <RingSegment
                  key={segment.id}
                  cx={cx}
                  cy={cy}
                  innerR={rings.products.innerR}
                  outerR={rings.products.outerR}
                  midR={rings.products.midR}
                  startAngle={startAngle}
                  endAngle={endAngle}
                  label={segment.label}
                  color={cat?.color ?? "var(--status-neutral)"}
                  outline={cat?.outline ?? true}
                  isDim={isD}
                  onHover={() => segSelect.setHovered(seg)}
                  onLeave={() => segSelect.setHovered(null)}
                  onClick={(e) =>
                    segSelect.select(seg, {
                      kind: "segment",
                      id: seg.id,
                      index: i,
                      group: "products",
                      element: e.currentTarget,
                    })
                  }
                  fontSize={10}
                />
              );
            })}

            {foundationSegs.map((segment, i) => {
              const startAngle = (360 / nF) * i + foundationGap / 2;
              const endAngle = (360 / nF) * (i + 1) - foundationGap / 2;
              const isH = effective?.id === segment.id;
              const isD = effective !== null && !isH;
              const seg: HoveredSegment = {
                id: segment.id,
                ring: eco.foundation.label,
                label: segment.label,
                categoryId: segmentKey(segment),
              };
              const cat = categoryOf(segment);
              return (
                <RingSegment
                  key={segment.id}
                  cx={cx}
                  cy={cy}
                  innerR={rings.foundation.innerR}
                  outerR={rings.foundation.outerR}
                  midR={rings.foundation.midR}
                  startAngle={startAngle}
                  endAngle={endAngle}
                  label={segment.label}
                  color={cat?.color ?? "var(--status-neutral)"}
                  outline={cat?.outline ?? true}
                  isDim={isD}
                  onHover={() => segSelect.setHovered(seg)}
                  onLeave={() => segSelect.setHovered(null)}
                  onClick={(e) =>
                    segSelect.select(seg, {
                      kind: "segment",
                      id: seg.id,
                      index: i,
                      group: "foundation",
                      element: e.currentTarget,
                    })
                  }
                  fontSize={11}
                />
              );
            })}

            <g opacity={effective ? 0.4 : 1} style={{ transition: "opacity 0.15s" }}>
              <circle cx={cx} cy={cy} r={rings.core.r} fill="var(--status-1)" />
              {eco.core.lines.map((line, i, arr) => (
                <text
                  key={i}
                  x={cx}
                  y={cy + (i - (arr.length - 1) / 2) * 14 + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="var(--bg)"
                >
                  {stripInlineMarkdown(line)}
                </text>
              ))}
            </g>
          </svg>
        </div>

        <div className="w-full pt-6 border-t border-dashed border-[var(--border-dashed)]">
          <AnimatedTabPanel
            activeKey={effective ? `segment:${effective.id}` : "ring-counts"}
            layoutMode="position"
            className="w-full"
          >
            {effective ? (
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span
                  className={cn(
                    "text-xs text-[var(--subtle)] min-w-[80px] leading-tight",
                    MODULE_LABEL_WRAP.label,
                  )}
                >
                  {stripInlineMarkdown(effective.ring)}
                </span>
                <span
                  className={cn(
                    "text-sm text-[var(--fg)] font-medium flex-1 min-w-0 leading-tight",
                    MODULE_LABEL_WRAP.row,
                  )}
                >
                  {stripInlineMarkdown(effective.label)}
                </span>
                <span
                  className="text-xs shrink-0"
                  style={{ color: effectiveCategory?.infoColor ?? "var(--subtle)" }}
                >
                  {effectiveCategory?.label ?? inactiveText}
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <RingCounts
                  label={eco.foundation.label}
                  counts={countsFor(foundationSegs)}
                />
                <RingCounts
                  label={eco.products.label}
                  counts={countsFor(productsSegs)}
                />
              </div>
            )}
          </AnimatedTabPanel>
        </div>
      </div>
    </div>
  );
}
